/** Pendant zu `Services/API/ApiContainerService.cs`. */

import type { ContainerCloneDefinition } from '@/types/definitions';
import type { Container, Document, OrderPosition, ReceiptAssignment } from '@/types/entities';
import { EntityTypes } from '@/types/enums';

import { buildAppPath } from '../apiBase';
import {
  createViewApi,
  fetchDocuments,
  postRecalculation,
  toMutationResult,
  type MutationResult,
  type ViewApi,
} from '../apiViewBase';
import { apiClient } from '../httpClient';

const ENDPOINT = 'container';

export const containerApi: ViewApi<Container> & {
  reCalc(viewId: number, id: number): Promise<Container>;
  clone(viewId: number, definition: ContainerCloneDefinition): Promise<MutationResult>;
  getDocuments(viewId: number, id: number): Promise<Document[]>;
  getOrderPositions(viewId: number, id: number): Promise<OrderPosition[]>;
  getReceiptAssignments(viewId: number, id: number): Promise<ReceiptAssignment[]>;
} = {
  ...createViewApi<Container>(ENDPOINT, EntityTypes.Container),

  reCalc: (viewId, id) =>
    postRecalculation<Container>(
      `${buildAppPath(ENDPOINT)}/${viewId}/${id}/recalc`,
      'Fehler beim Neukalkulieren der Frachtkosten.',
    ),

  clone: async (viewId, definition) =>
    toMutationResult(
      await apiClient.raw('POST', `${buildAppPath(ENDPOINT)}/${viewId}/clone`, definition),
    ),

  getDocuments: (viewId, id) => fetchDocuments(ENDPOINT, viewId, id),

  /**
   * `GET container/{viewId}/{id}/bestellpositionen` — `ContainerController.GetBestellpositionen`
   * gibt die Teilsammlung als reines Array zurück (`GetSubCollection<OrderPosition>`); fehlende
   * Berechtigung meldet 401, ein unbekannter Container 404.
   *
   * Anders als die übrigen Methoden schluckt diese den Fehler **nicht**: Die Schublade zeigt
   * daraus einen Fehlerzustand mit „Erneut versuchen" (REQ-27 §2.2).
   */
  getOrderPositions: (viewId, id) =>
    apiClient.get<OrderPosition[]>(`${buildAppPath(ENDPOINT)}/${viewId}/${id}/bestellpositionen`),

  /** `GET container/{viewId}/{id}/rechnungszuordnungen` — wie {@link getOrderPositions}. */
  getReceiptAssignments: (viewId, id) =>
    apiClient.get<ReceiptAssignment[]>(
      `${buildAppPath(ENDPOINT)}/${viewId}/${id}/rechnungszuordnungen`,
    ),
};
